import { useState, useMemo, useCallback } from "react";
import { shuffleArray } from "../utils/arrayUtils";
import { normalizeString } from "../utils/normalizeString";
const applyStatutCommand = (images, command) => {
  const cmd = command.toLowerCase().trim();
  /* =========================
     !statut / !statut-x
     ========================= */
  // !statut → statut non vide
  if (cmd === "!statut") {
    return images.filter((img) => img.statut && img.statut.trim() !== "");
  }

    if (cmd === "!new") {
    return images.filter((img) => img.add && img.add.trim() !== "");
  }

  // !statut-x / !statut-m
  const match = cmd.match(/^!statut-(.+)$/);
  if (match) {
    const needle = match[1];
    return images.filter(
      (img) => img.statut && img.statut.toLowerCase().includes(needle),
    );
  }

  /* =========================
     !note-*
     ========================= */
  if (cmd.startsWith("!note-")) {
    const value = cmd.replace("!note-", "");
    return images.filter(
      (img) =>
        img.normalizedNote &&
        img.normalizedNote.toLowerCase().includes(normalizeString(value)),
    );
  }

  // appel de tri ne fonctionne pas, trop complexe à corriger par rapport au gain
  /* =========================
     !sort-random
     ========================= */
  /*if (cmd === "!sort-random") {
    return shuffleArray(images);
  }*/

  /* =========================
     !sort-nyme-(true|false)
     ========================= */
  /*if (cmd.startsWith("!sort-nyme")) {
    const [, , flag] = cmd.split("-");
    const asc = flag !== "false";

    return [...images].sort((a, b) => {
      const A = a.nyme ?? "";
      const B = b.nyme ?? "";
      return asc
        ? A.localeCompare(B, "fr", { sensitivity: "base" })
        : B.localeCompare(A, "fr", { sensitivity: "base" });
    });
  }*/

  // commande inconnue
  return [];
};

// 👉 Normalise pour recherche sans accents ni casse
/*
function normalizeString(str = "") {
  return str
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}
*/
// 👉 extrait le nom de rue, le numéro, et le suffixe ("bis", "ter", "(2)", etc.)
function parseAddressParts(img) {
  const rawStreet = (img["adressS"] || "").toString();
  const rawNum = (img["num"] || "").toString();

  const street = normalizeString(rawStreet);

  // ex : "21 bis" / "23 (2)" / "7A" / "12-14"
  const combined = `${rawNum}`.trim();

  // capture :
  //  - nombre principal
  //  - suffixe lettre/bis/ter
  //  - parenthèses
  const match = combined.match(
    /^(\d+)\s*(?:[-/]?\s*\d+)?\s*([A-Za-z]+|bis|ter|quater)?\s*(?:\((\d+)\))?/i,
  );

  let main = null;
  let suffix = "";
  let alt = null;

  if (match) {
    main = parseInt(match[1], 10);
    suffix = (match[2] || "").toLowerCase();
    alt = match[3] ? parseInt(match[3], 10) : null;
  }

  return {
    street,
    main, // numéro principal (number or null)
    suffix, // bis, ter, a/b/c…
    alt, // numéro dans parenthèses
    raw: combined,
  };
}

// ordre des suffixes usuels
const SUFFIX_ORDER = {
  "": 0,
  a: 1,
  b: 2,
  c: 3,
  bis: 4,
  ter: 5,
  quater: 6,
};

export function useSort(images, searchAdresse, searchMaison) {
  const [sortKey, setSortKey] = useState("");

  const [sortNomActive, setSortNomActive] = useState(false);
  const [sortNomAsc, setSortNomAsc] = useState(true);

  const [sortAdresseActive, setSortAdresseActive] = useState(false);
  const [sortAdresseAsc, setSortAdresseAsc] = useState(true);

  const [sortDateActive, setSortDateActive] = useState(false);
  const [sortDateAsc, setSortDateAsc] = useState(true);

  const [shuffleSeed, setShuffleSeed] = useState(0);

  const [selectedPeriod, setSelectedPeriod] = useState(null);

  const shuffle = useCallback(() => {
    setShuffleSeed((s) => s + 1);
  }, []);

  // ✅ Fonction de comparaison stable
  const specialNameCompare = useCallback((a, b) => {
    const sa = (a["nyme"] || "").toString().trim();
    const sb = (b["nyme"] || "").toString().trim();

    const startsWithLetter = (s) => /^[A-Za-zÀ-ÖØ-öø-ÿ]/.test(s);
    const la = startsWithLetter(sa);
    const lb = startsWithLetter(sb);

    if (la && !lb) return -1;
    if (!la && lb) return 1;

    return sa.localeCompare(sb, "fr", { sensitivity: "base" });
  }, []);

  // ✅ Extraction de tag stable
  const getSortTag = useCallback((img) => {
    const typolobis = img?.["typoloAbis"];
    const typolo = img?.["typoloA"];

    return typolobis !== undefined && typolobis !== null && typolobis !== ""
      ? String(typolobis)
      : String(typolo || "");
  }, []);

  // ✅ Normalisation des termes de recherche
  const normalizedSearchAdresse = useMemo(
    () => normalizeString(searchAdresse || ""),
    [searchAdresse],
  );

  const normalizedSearchMaison = useMemo(
    () => normalizeString(searchMaison || ""),
    [searchMaison],
  );

  // ✅ Filtrage par recherche optimisé
  const searchedImages = useMemo(() => {
    if (!normalizedSearchAdresse && !normalizedSearchMaison) {
      return images;
    }

    if (normalizedSearchMaison) {
      if (normalizedSearchMaison.startsWith("!")) {
        return applyStatutCommand(images, normalizedSearchMaison);
      }
    }

    return images.filter((img) => {
      if (normalizedSearchAdresse) {
        const adresse = normalizeString(img["adressB"] || "");
        if (!adresse.includes(normalizedSearchAdresse)) return false;
      }

      if (normalizedSearchMaison) {
        const maison = normalizeString(img["nyme"] || "");
        if (!maison.includes(normalizedSearchMaison)) return false;
      }

      return true;
    });
  }, [images, normalizedSearchAdresse, normalizedSearchMaison]);

  // ✅ Tri optimisé avec early return
  const finalSortedImages = useMemo(() => {
    const hasMaisonSearch =
      normalizedSearchMaison && !normalizedSearchMaison.startsWith("!");

    const hasAdresseSearch = !!normalizedSearchAdresse; /**/

    // Pas de tri actif
    if (
      !sortNomActive &&
      !sortAdresseActive &&
      !sortDateActive &&
      !sortDateActive &&
      !selectedPeriod &&
      !sortKey
    ) {
      // 🔍 TRI AUTO LIE À LA RECHERCHE

      // recherche maison → tri par nom
      if (hasMaisonSearch) {
        const arr = [...searchedImages];
        arr.sort((a, b) => specialNameCompare(a, b));
        return arr;
      }

      // recherche rue → tri par adresse
      if (hasAdresseSearch) {
        const arr = [...searchedImages];
        arr.sort((a, b) => {
          const A = parseAddressParts(a);
          const B = parseAddressParts(b);

          if (A.street !== B.street) {
            return A.street.localeCompare(B.street, "fr", {
              sensitivity: "base",
            });
          }

          if (A.main !== B.main) {
            if (A.main == null) return 1;
            if (B.main == null) return -1;
            return A.main - B.main;
          }

          if (A.suffix !== B.suffix) {
            const sa = SUFFIX_ORDER[A.suffix] ?? 99;
            const sb = SUFFIX_ORDER[B.suffix] ?? 99;
            return sa - sb;
          }

          if (A.alt !== B.alt) {
            if (A.alt == null) return 1;
            if (B.alt == null) return -1;
            return A.alt - B.alt;
          }

          return A.raw.localeCompare(B.raw, "fr", {
            sensitivity: "base",
          });
        });

        return arr;
      }

      // 🎲 shuffle éventuel
      if (shuffleSeed > 0) {
        return shuffleArray(searchedImages);
      }

      return searchedImages;
    }

    const arr = [...searchedImages];

    /* * * * * * * * * * */
    /* * Tri par nom * * */
    /* * * * * * * * * * */
    if (sortNomActive) {
      arr.sort((a, b) => {
        const cmp = specialNameCompare(a, b);
        return sortNomAsc ? cmp : -cmp;
      });
      return arr;
    }

    /* * * * * * * * * * */
    /* Tri par adresse * */
    /* * * * * * * * * * */
    // 👉 Tri par adresse avec vrai tri numérique + suffixes
    if (sortAdresseActive) {
      arr.sort((a, b) => {
        const A = parseAddressParts(a);
        const B = parseAddressParts(b);

        // 1) comparer nom de rue normalisé
        if (A.street !== B.street) {
          const cmpStreet = A.street.localeCompare(B.street, "fr", {
            sensitivity: "base",
          });
          return sortAdresseAsc ? cmpStreet : -cmpStreet;
        }

        // 2) comparer numéro principal
        if (A.main !== B.main) {
          // mettre les nulls à la fin
          if (A.main == null) return 1;
          if (B.main == null) return -1;

          const cmpNum = A.main - B.main;
          return sortAdresseAsc ? cmpNum : -cmpNum;
        }

        // 3) comparer suffixes (bis, ter, a, b, c…)
        if (A.suffix !== B.suffix) {
          const sa = SUFFIX_ORDER[A.suffix] ?? 99;
          const sb = SUFFIX_ORDER[B.suffix] ?? 99;
          const cmpSuf = sa - sb;
          return sortAdresseAsc ? cmpSuf : -cmpSuf;
        }

        // 4) comparer numéro secondaire entre parenthèses
        if (A.alt !== B.alt) {
          if (A.alt == null) return 1;
          if (B.alt == null) return -1;

          const cmpAlt = A.alt - B.alt;
          return sortAdresseAsc ? cmpAlt : -cmpAlt;
        }

        // 5) fallback brut
        const cmpRaw = A.raw.localeCompare(B.raw, "fr", {
          sensitivity: "base",
        });
        return sortAdresseAsc ? cmpRaw : -cmpRaw;
      });

      return arr;
    }
    /* * * * * * * * * * */
    /* * Tri par date  * */
    /* * * * * * * * * * */
    /*
    if (sortDateActive) {
      // 1️⃣ EXCLUSION DES IMAGES SANS DATE VALIDE
      let dated = arr.filter((img) => img.sortDate);
      // 2️⃣ TRI CHRONOLOGIQUE
      dated.sort((a, b) => {
        const getStart = (d) => (d.type === "point" ? d.year : d.start);

        return sortDateAsc
          ? getStart(a.sortDate) - getStart(b.sortDate)
          : getStart(b.sortDate) - getStart(a.sortDate);
      });

      if (selectedPeriod) {
        const { start, end } = selectedPeriod;

        dated = dated.filter((img) => {
          const d = img.sortDate;

          if (d.type === "point") {
            return d.year >= start && d.year <= end;
          }

          return d.start >= start && d.end <= end;
        });
      }
      return dated;
    }
*/
    //console.log("sortDateActive",sortDateActive,"selectedPeriod",selectedPeriod);
    if (sortDateActive || selectedPeriod) {
      // 1️⃣ EXCLUSION DES IMAGES SANS DATE VALIDE
      let dated = arr.filter((img) => img.sortDate);

      if (sortDateActive) {
        // 2️⃣ TRI CHRONOLOGIQUE
        dated.sort((a, b) => {
          const getStart = (d) => (d.type === "point" ? d.year : d.start);

          return sortDateAsc
            ? getStart(a.sortDate) - getStart(b.sortDate)
            : getStart(b.sortDate) - getStart(a.sortDate);
        });
      }

      if (selectedPeriod) {
        const { start, end } = selectedPeriod;

        dated = dated.filter((img) => {
          const d = img.sortDate;

          if (d.type === "point") {
            return d.year >= start && d.year <= end;
          }

          return d.start >= start && d.end <= end;
        });
      }
      return dated;
    }

    /* * * * * * * * * * */
    // Tri par clé générique
    /* * * * * * * * * * */
    if (sortKey === "typoloA") {
      arr.sort((a, b) => {
        const va = getSortTag(a).toLowerCase();
        const vb = getSortTag(b).toLowerCase();
        return va.localeCompare(vb, "fr", { sensitivity: "base" });
      });
      return arr;
    }

    if (sortKey) {
      arr.sort((a, b) => {
        const va = (a[sortKey] || "").toString().toLowerCase();
        const vb = (b[sortKey] || "").toString().toLowerCase();
        return va.localeCompare(vb, "fr", { sensitivity: "base" });
      });
    }

    return arr;
  }, [
    searchedImages,
    sortNomActive,
    sortNomAsc,
    sortAdresseActive,
    sortAdresseAsc,
    sortKey,
    specialNameCompare,
    getSortTag,
    shuffleSeed,
    sortDateActive,
    sortDateAsc,
    selectedPeriod,
    normalizedSearchAdresse,
    normalizedSearchMaison,
  ]);

  const resetSort = useCallback(() => {
    setSortNomActive(false);
    setSortAdresseActive(false);
    setSortDateActive(false);
    setSelectedPeriod(null);

    setSortKey("");
  }, []);
  /*
  // ✅ Toggle handlers optimisés
  const toggleSortNom_old = useCallback(() => {
    setSortNomActive((prev) => {
      if (prev) {
        setSortNomAsc((s) => !s);
        return true;
      } else {
        setSortNomAsc(true);
        setSortAdresseActive(false);
        return true;
      }
    });
  }, []);
  const toggleSortAdresse_old = useCallback(() => {
    setSortAdresseActive((prev) => {
      if (prev) {
        setSortAdresseAsc((s) => !s);
        return true;
      } else {
        setSortAdresseAsc(true);
        setSortNomActive(false);
        return true;
      }
    });
  }, []);*/
  const toggleSortNom = useCallback(() => {
    setSortNomActive((active) => {
      if (!active) {
        setSortNomAsc(true);
        setSortAdresseActive(false);
        setSortDateActive(false);
        setSelectedPeriod(null);

        return true;
      }

      setSortNomAsc((v) => !v);
      return true;
    });
  }, []);

  const toggleSortAdresse = useCallback(() => {
    setSortAdresseActive((active) => {
      if (!active) {
        setSortAdresseAsc(true);
        setSortNomActive(false);
        setSortDateActive(false);
        setSelectedPeriod(null);

        return true;
      }

      setSortAdresseAsc((v) => !v);
      return true;
    });
  }, []);

  const toggleSortDate = useCallback(() => {
    setSortDateActive((active) => {
      //.log("toggleSortDate", active);
      if (!active) {
        setSortDateAsc(true);
        setSortNomActive(false);
        setSortAdresseActive(false);
        return true;
      } else {
        //setSelectedPeriod(null);
      }
      setSortDateAsc((v) => !v);
      return true;
    });
  }, []);

  const setPeriod = (period) => {
    /* if (period && !sortDateActive) {
      setSortDateActive(true);
      setSortDateAsc(true);
    } */ // activer si tri period pas séparé de tri date

    setSelectedPeriod(period);
  };

  /*useEffect(() => {
    console.log("→ sortAdresseAsc ACTUEL =", sortAdresseAsc);
  }, [sortAdresseAsc]);

  useEffect(() => {
    console.log("→ sortNomAsc ACTUEL =", sortNomAsc);
  }, [sortNomAsc]);*/

  return {
    sortKey,
    setSortKey,
    sortNomActive,
    sortNomAsc,
    sortAdresseActive,
    sortAdresseAsc,
    toggleSortNom,
    toggleSortAdresse,
    finalSortedImages,
    shuffle,
    resetSort,
    sortDateActive,
    sortDateAsc,
    toggleSortDate,
    selectedPeriod,
    //setSelectedPeriod,
    setPeriod,
  };
}
